import {
  adminBroadcastImageSchema,
  adminBroadcastSchema,
  type AdminBroadcast,
  type AdminBroadcastCreateRequest,
  type AdminBroadcastImage,
} from '@steado/admin-contract/broadcasts';
import { paginationMetaSchema } from '@steado/admin-contract/pagination';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient } from '@/lib/api/client';

import type { BroadcastsSearch } from './broadcasts-search';

export const BROADCASTS_PAGE_SIZE = 20;

const broadcastsPageSchema = z.object({
  items: z.array(adminBroadcastSchema),
  pagination: paginationMetaSchema,
});
export type BroadcastsPage = z.infer<typeof broadcastsPageSchema>;

export function broadcastsQueryOptions(api: ApiClient, search: BroadcastsSearch) {
  const query = {
    status: search.status,
    cursor: search.cursor,
    limit: BROADCASTS_PAGE_SIZE,
  };
  return queryOptions({
    queryKey: ['admin', 'broadcasts', query],
    queryFn: async (): Promise<BroadcastsPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/notifications/broadcasts',
        query,
        schema: z.array(adminBroadcastSchema),
      });
      return broadcastsPageSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    placeholderData: (previous) => previous,
  });
}

export function createBroadcast(api: ApiClient, body: AdminBroadcastCreateRequest): Promise<AdminBroadcast> {
  return api.request({
    path: '/admin/notifications/broadcasts',
    method: 'POST',
    body,
    schema: adminBroadcastSchema,
  });
}

/**
 * Stores the image first and returns its key, which the create request then
 * references. Two steps rather than one multipart create, so an operator sees
 * a rejected image before they have written the copy.
 */
export function uploadBroadcastImage(api: ApiClient, file: File): Promise<AdminBroadcastImage> {
  const form = new FormData();
  form.append('file', file, file.name);

  return api.request({
    path: '/admin/notifications/broadcast-images',
    method: 'POST',
    body: form,
    schema: adminBroadcastImageSchema,
  });
}

export function cancelBroadcast(api: ApiClient, id: string): Promise<AdminBroadcast> {
  return api.request({
    path: `/admin/notifications/broadcasts/${id}/cancel`,
    method: 'POST',
    body: { reason: 'PRODUCT_ANNOUNCEMENT', confirmation: 'CANCEL_BROADCAST' },
    schema: adminBroadcastSchema,
  });
}
